import { useEffect, useRef, useState } from 'react';

import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import { ListNoData } from 'presentation/components/ListNoData';
import { openErrorNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppSelector } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { Pagination } from 'antd';
import { customItemRender } from './components/CustomItemRender';
import { CardComponent } from './components/Card';
import { TYPE_FILTER } from './constant';
import { CU049SearchParams } from './models/Model_CU-049-01';

interface props {
    propertyUseCase: PropertyUseCaseInterface;
}

const CU04901Page = ({ propertyUseCase }: props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const fetchingCount = useAppSelector((state) => state.app.fetchingCount);

    const [dataSearch, setDataSearch] = useState<any>();
    const [listProperty, setListProperty] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [numPages, setNumPages] = useState<number>(1);
    const [totalRecord, setTotalRecord] = useState<number>(0);
    const pageSize = 12;
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const shouldScrollAfterFetchRef = useRef<boolean>(false);

    useEffect(() => {
        const searchCondition = location.state.dataSearch;
        setDataSearch(searchCondition);
    }, []);

    useEffect(() => {
        if (!dataSearch) return;
        const {
            typeFilter,
            oldMax,
            oldMin,
            keyword,
            areaCode,
            companyCds,
            lineCds,
            stationCds,
            listAreaCodes2,
            listAreaCodes3,
        } = dataSearch;
        const companyCd = companyCds ? companyCds.join(',') : null;
        const lineCd = lineCds ? lineCds.join(',') : null;
        const stationCd = stationCds ? stationCds.join(',') : null;
        const listAreaCode2 = listAreaCodes2 ? listAreaCodes2.join(',') : null;
        const listAreaCode3 = listAreaCodes3 ? listAreaCodes3.join(',') : null;
        switch (typeFilter) {
            case TYPE_FILTER.fromCU05501:
                getMasterPropertyFilterStation({
                    keyword,
                    page: page,
                    pageSize: 12,
                    buyNowPriceMin: 0,
                    buyNowPriceMax: 4000,
                    oldMin: 0,
                    oldMax: 40,
                });
                break;
            case TYPE_FILTER.fromCU05504:
                getMasterPropertyFilterArea({
                    page: page,
                    pageSize: 12,
                    minAge: +oldMin,
                    maxAge: +oldMax,
                    areaCode: +areaCode,
                    listAreaCode2: listAreaCode2,
                    listAreaCode3: listAreaCode3,
                });
                break;
            case TYPE_FILTER.fromCU05505ByName:
                getMasterPropertyFilterStation({
                    page: page,
                    pageSize: 12,
                    areaCode: +areaCode,
                    keyword: keyword,
                });
                break;
            case TYPE_FILTER.fromCU05505:
                getMasterPropertyFilterStation({
                    page: page,
                    pageSize: 12,
                    areaCode: areaCode,
                    oldMin: +oldMin,
                    oldMax: +oldMax,
                    companyCd: companyCd,
                    stationCd: stationCd,
                    lineCd: lineCd,
                    typeFilter: 'station',
                    stationName: dataSearch.searchCompany,
                });
                break;
        }
    }, [page, dataSearch]);

    useEffect(() => {
        if (fetchingCount !== 0 || !shouldScrollAfterFetchRef.current) return;

        const scrollToTop = () => {
            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToTop();
                setTimeout(scrollToTop, 0);
                shouldScrollAfterFetchRef.current = false;
            });
        });
    }, [fetchingCount]);

    const getMasterPropertyFilterStation = async (params: CU049SearchParams) => {
        try {
            dispatch(startFetching());
            const response: any = await propertyUseCase.getMasterPropertyFilterStation({
                ...params,
                isLogin: 0,
            });
            setNumPages(response?.result?.pagination?.numPages);
            setTotalRecord(response?.result?.pagination?.count);
            setListProperty(response?.result.data ?? []);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const getMasterPropertyFilterArea = async (params: CU049SearchParams) => {
        try {
            dispatch(startFetching());
            const response: any = await propertyUseCase.getMasterPropertyFilterArea({
                ...params,
                isLogin: 0,
            });
            setNumPages(response?.result?.pagination?.numPages);
            setTotalRecord(response?.result?.pagination?.count);
            setListProperty(response?.result.data ?? []);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const handlePageChange = (newPage: number) => {
        shouldScrollAfterFetchRef.current = true;
        setPage(newPage);
    };

    const handleViewDetail = (value: number) => {
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_049_02}/${value}`);
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
                textAlign: 'center',
            }}
            className="CU-049-01-container"
        >
            <section
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingRight: 40,
                }}
            >
                <PageTitle title="物件検索" />
                {totalRecord > 0 && (
                    <div style={{ fontSize: 16, fontWeight: 500, color: '#000000' }}>
                        検索結果：{totalRecord}件
                    </div>
                )}
            </section>

            {listProperty?.length > 0 ? (
                <section
                    ref={scrollContainerRef}
                    style={{
                        height: '100%',
                        overflow: 'auto',
                        flexGrow: 1,
                    }}
                >
                    <div style={{ width: 'fit-content' }}>
                        <div className="grid-container">
                            {listProperty.map((element: any, index: number) => (
                                <CardComponent
                                    key={index}
                                    data={element}
                                    onClickViewDetail={(e) => handleViewDetail(e)}
                                />
                            ))}
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: 20,
                            marginBottom: 20,
                        }}
                    >
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={totalRecord}
                            showLessItems
                            showSizeChanger={false}
                            onChange={handlePageChange}
                            itemRender={customItemRender}
                            style={{ margin: '20px 0', textAlign: 'center' }}
                        />
                    </div>
                </section>
            ) : (
                <ListNoData />
            )}
        </div>
    );
};

export default CU04901Page;
